import { memo } from "react";
import ToggleButton from "@mui/material/ToggleButton";

import {
  consentAudienceHelper,
  consentAudienceLabel,
  getAvailablePatientFinderAudiences,
  isPatientFinderAudience,
  type PatientFinderAudience,
} from "../helpers/patientFinderAudience";
import CommunicationsPatientFinderFilterCard from "./CommunicationsPatientFinderFilterCard";
import CommunicationsSegmentedFilterControl from "./CommunicationsSegmentedFilterControl";

export interface CommunicationsPatientFinderAudienceFilterProps {
  value: PatientFinderAudience;
  onChange: (audience: PatientFinderAudience) => void;
  /**
   * Públicos exibidos, na ordem dos botões.
   * Sem a prop: as quatro opções, com "Todos" primeiro.
   */
  audiences?: PatientFinderAudience[];
  appointmentCount: number;
  consentCount: number;
  registeredCount: number;
  /** Total de "Todos", já sem repetir a mesma pessoa. */
  allCount: number;
  /** Algum público extra ainda carregando (mostra "…" no lugar da contagem). */
  loading?: boolean;
  disabled?: boolean;
  /**
   * Ecommerce: o público de consentimento é a lista inteira.
   * O rótulo permanece "Com consentimento".
   */
  isEcommerceOrg?: boolean;
}

/** Contagem entre parênteses; "…" enquanto a lista ainda não chegou. */
function countLabel(count: number, loading: boolean): string {
  return loading ? "(…)" : `(${count})`;
}

const LABEL_BY_AUDIENCE: Record<
  Exclude<PatientFinderAudience, "consent">,
  string
> = {
  all: "Todos",
  appointments: "Pacientes atendidos",
  registered: "Clientes cadastrados",
};

const HELPER_BY_AUDIENCE: Record<
  Exclude<PatientFinderAudience, "consent">,
  string
> = {
  appointments: "Pacientes com consulta nas unidades da clínica.",
  registered:
    "Clientes cadastrados não têm consultas: o filtro de consultas fica oculto.",
  all: "Junta os públicos sem repetir a mesma pessoa. Com filtro de consultas, só entram pacientes.",
};

/**
 * Escolha do público no "Encontrar pacientes".
 * Só aparece quando o chamador fornece consentimento e/ou clientes cadastrados.
 */
const CommunicationsPatientFinderAudienceFilter = memo(
  ({
    value,
    onChange,
    audiences = getAvailablePatientFinderAudiences(false),
    appointmentCount,
    consentCount,
    registeredCount,
    allCount,
    loading = false,
    disabled = false,
    isEcommerceOrg = false,
  }: CommunicationsPatientFinderAudienceFilterProps) => {
    const labelByAudience: Record<PatientFinderAudience, string> = {
      ...LABEL_BY_AUDIENCE,
      consent: consentAudienceLabel(isEcommerceOrg),
    };
    const helperByAudience: Record<PatientFinderAudience, string> = {
      ...HELPER_BY_AUDIENCE,
      consent: consentAudienceHelper(isEcommerceOrg),
    };

    // "Pacientes atendidos" já está carregado; os demais dependem do `loading`.
    const countByAudience: Record<PatientFinderAudience, string> = {
      all: countLabel(allCount, loading),
      appointments: countLabel(appointmentCount, false),
      consent: countLabel(consentCount, loading),
      registered: countLabel(registeredCount, loading),
    };

    return (
      <CommunicationsPatientFinderFilterCard
        title="Público"
        helperText={helperByAudience[value]}
      >
        <CommunicationsSegmentedFilterControl
          value={value}
          disabled={disabled}
          onChange={(_, next: string | null) => {
            if (next != null && isPatientFinderAudience(next)) onChange(next);
          }}
          aria-label="Escolher público da busca"
        >
          {audiences.map((audience) => (
            <ToggleButton key={audience} value={audience}>
              {labelByAudience[audience]} {countByAudience[audience]}
            </ToggleButton>
          ))}
        </CommunicationsSegmentedFilterControl>
      </CommunicationsPatientFinderFilterCard>
    );
  },
);

CommunicationsPatientFinderAudienceFilter.displayName =
  "CommunicationsPatientFinderAudienceFilter";

export default CommunicationsPatientFinderAudienceFilter;
